import { WorkStep } from '../ui/WorkStep';

export function HowItWorks() {
  return (
    <section id="how-it-works" className="py-20 bg-[#F6F8FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-base font-semibold text-[#0969DA] tracking-wide uppercase">How It Works</h2>
          <p className="mt-4 text-lg text-[#57606A]">Get started in four simple steps</p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Step 1: Upload Repository */}
          <div className="bg-white rounded-lg p-6 border border-[#D0D7DE] hover:bg-[#F3F4F6] transition-all duration-300 shadow-sm">
            <div className="h-64 mb-4 flex items-center justify-center">
              <svg className="w-full h-full" viewBox="0 0 300 300">
                <rect width="300" height="300" fill="white"/>
                {/* Terminal Window */}
                <rect x="20" y="20" width="260" height="260" rx="4" fill="#F6F8FA" stroke="#D0D7DE"/>
                <rect x="20" y="20" width="260" height="30" rx="4" fill="#D0D7DE"/>
                <circle cx="35" cy="35" r="5" fill="#D73A49"/>
                <circle cx="50" cy="35" r="5" fill="#F1E05A"/>
                <circle cx="65" cy="35" r="5" fill="#2188FF"/>
                {/* Code Lines */}
                <text x="40" y="80" fontFamily="JetBrains Mono" fontSize="16" fill="#2188FF">$ git clone</text>
                <text x="40" y="110" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">https://github.com/</text>
                <text x="40" y="140" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">your-repo.git</text>
                <text x="40" y="180" fontFamily="JetBrains Mono" fontSize="16" fill="#2188FF">$ firstcommit</text>
                <text x="40" y="210" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">Analyzing...</text>
                <text x="40" y="240" fontFamily="JetBrains Mono" fontSize="16" fill="#2DA44E">✓ Ready</text>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-[#24292F]">Upload Your Repository</h3>
            <p className="mt-2 text-[#57606A]">Connect your GitHub repository and let AI analyze your codebase</p>
          </div>

          {/* Step 2: Analyze Code Structure */}
          <div className="bg-white rounded-lg p-6 border border-[#D0D7DE] hover:bg-[#F3F4F6] transition-all duration-300 shadow-sm">
            <div className="h-64 mb-4 flex items-center justify-center">
              <svg className="w-full h-full" viewBox="0 0 300 300">
                <rect width="300" height="300" fill="white"/>
                {/* Code Structure Diagram */}
                <rect x="20" y="20" width="260" height="260" rx="4" fill="#F6F8FA" stroke="#D0D7DE"/>
                <rect x="20" y="20" width="260" height="30" rx="4" fill="#D0D7DE"/>
                <circle cx="35" cy="35" r="5" fill="#D73A49"/>
                <circle cx="50" cy="35" r="5" fill="#F1E05A"/>
                <circle cx="65" cy="35" r="5" fill="#2188FF"/>
                {/* Tree Structure */}
                <line x1="150" y1="80" x2="150" y2="120" stroke="#D0D7DE"/>
                <line x1="150" y1="120" x2="80" y2="160" stroke="#D0D7DE"/>
                <line x1="150" y1="120" x2="220" y2="160" stroke="#D0D7DE"/>
                <line x1="150" y1="120" x2="150" y2="200" stroke="#D0D7DE"/>
                <circle cx="150" cy="80" r="12" fill="#2188FF"/>
                <circle cx="80" cy="160" r="12" fill="#D73A49"/>
                <circle cx="220" cy="160" r="12" fill="#F1E05A"/>
                <circle cx="150" cy="200" r="12" fill="#2DA44E"/>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-[#24292F]">Analyze Code Structure</h3>
            <p className="mt-2 text-[#57606A]">AI understands your project architecture and dependencies</p>
          </div>

          {/* Step 3: Interactive Exploration */}
          <div className="bg-white rounded-lg p-6 border border-[#D0D7DE] hover:bg-[#F3F4F6] transition-all duration-300 shadow-sm">
            <div className="h-64 mb-4 flex items-center justify-center">
              <svg className="w-full h-full" viewBox="0 0 300 300">
                <rect width="300" height="300" fill="white"/>
                {/* Code Navigation Interface */}
                <rect x="20" y="20" width="260" height="260" rx="4" fill="#F6F8FA" stroke="#D0D7DE"/>
                <rect x="20" y="20" width="260" height="30" rx="4" fill="#D0D7DE"/>
                <circle cx="35" cy="35" r="5" fill="#D73A49"/>
                <circle cx="50" cy="35" r="5" fill="#F1E05A"/>
                <circle cx="65" cy="35" r="5" fill="#2188FF"/>
                {/* Code Navigation */}
                <rect x="30" y="60" width="80" height="200" fill="#F6F8FA" stroke="#D0D7DE"/>
                <text x="35" y="85" fontFamily="JetBrains Mono" fontSize="16" fill="#2188FF">Files</text>
                <text x="35" y="125" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">→ src/</text>
                <text x="35" y="165" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">→ components/</text>
                <text x="35" y="205" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">→ utils/</text>
                <text x="35" y="245" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">→ tests/</text>
                <line x1="120" y1="60" x2="120" y2="260" stroke="#D0D7DE"/>
                <text x="130" y="85" fontFamily="JetBrains Mono" fontSize="16" fill="#2188FF">function</text>
                <text x="130" y="125" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">onboarding() {'{'}</text>
                <text x="130" y="165" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">  // AI powered</text>
                <text x="130" y="205" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">  // navigation</text>
                <text x="130" y="245" fontFamily="JetBrains Mono" fontSize="16" fill="#24292F">{'}'}</text>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-[#24292F]">Interactive Exploration</h3>
            <p className="mt-2 text-[#57606A]">Navigate through your codebase with AI-guided insights</p>
          </div>

          {/* Step 4: Faster Onboarding */}
          <div className="bg-white rounded-lg p-6 border border-[#D0D7DE] hover:bg-[#F3F4F6] transition-all duration-300 shadow-sm">
            <div className="h-64 mb-4 flex items-center justify-center">
              <svg className="w-full h-full" viewBox="0 0 300 300">
                <rect width="300" height="300" fill="white"/>
                {/* Team Collaboration */}
                <rect x="20" y="20" width="260" height="260" rx="4" fill="#F6F8FA" stroke="#D0D7DE"/>
                <rect x="20" y="20" width="260" height="30" rx="4" fill="#D0D7DE"/>
                <circle cx="35" cy="35" r="5" fill="#D73A49"/>
                <circle cx="50" cy="35" r="5" fill="#F1E05A"/>
                <circle cx="65" cy="35" r="5" fill="#2188FF"/>
                {/* Success Metrics */}
                <text x="40" y="80" fontFamily="JetBrains Mono" fontSize="16" fill="#2188FF">Onboarding Time</text>
                <line x1="40" y1="90" x2="240" y2="90" stroke="#D0D7DE"/>
                <line x1="40" y1="90" x2="140" y2="90" stroke="#2DA44E" strokeWidth="6"/>
                <text x="40" y="140" fontFamily="JetBrains Mono" fontSize="16" fill="#2188FF">Productivity</text>
                <line x1="40" y1="150" x2="240" y2="150" stroke="#D0D7DE"/>
                <line x1="40" y1="150" x2="200" y2="150" stroke="#2DA44E" strokeWidth="6"/>
                <text x="40" y="200" fontFamily="JetBrains Mono" fontSize="16" fill="#2DA44E">✓ 50% Faster</text>
                <text x="40" y="230" fontFamily="JetBrains Mono" fontSize="16" fill="#2DA44E">✓ 2x More</text>
                <text x="40" y="260" fontFamily="JetBrains Mono" fontSize="16" fill="#2DA44E">✓ 100% AI</text>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-[#24292F]">Get Onboarded Faster</h3>
            <p className="mt-2 text-[#57606A]">Reduce onboarding time and increase productivity</p>
          </div>
        </div>
      </div>
    </section>
  );
} 